import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { paymentLinkService, GenerateLinkRequest, GenerateLinkResponse } from '../../services/paymentLinkService';
import { FIAT_CURRENCIES } from '../../constants/fiatCurrencies';
import { exchangeRateService } from '../../services/exchangeRateService';
import { cn } from '../../utils/cn';
import {
  checkoutPathForSession,
  checkoutUrlForSession,
  extractPaySessionId,
} from '../../utils/paymentCheckoutNavigation';

const BRAND = '#2297D1';

const FIAT_PROVIDER_OPTIONS = [
  { id: 'onramper', label: 'Onramper' },
  { id: 'paybis', label: 'Paybis' },
  { id: 'transfi', label: 'TransFi' },
] as const;

function filterLinksByProviders(links: string[], selectedIds: Set<string>): string[] {
  if (selectedIds.size === FIAT_PROVIDER_OPTIONS.length) return links;
  return links.filter((link) => {
    const lower = link.toLowerCase();
    return Array.from(selectedIds).some((id) => lower.includes(id));
  });
}

function extractUrl(link: string): string {
  const match = link.match(/https:\/\/[^\s]+/);
  return match ? match[0] : link;
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybeAxios = err as { response?: { data?: unknown } };
    const data = maybeAxios.response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      if (typeof obj.message === 'string' && obj.message.trim()) return obj.message.trim();
      if (typeof obj.detail === 'string' && obj.detail.trim()) return obj.detail.trim();
      if (typeof obj.title === 'string' && obj.title.trim()) return obj.title.trim();
    }
  }
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return fallback;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDaysISO(base: Date, days: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return formatISODate(d);
}

function generateInvoiceNumber(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const r = Math.floor(100 + Math.random() * 900);
  return `INV-${y}${m}${day}-${r}`;
}

interface InvoiceLineItem {
  id: string;
  name: string;
  qty: number;
  price: number;
}

function newLineId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const IssueInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const [fiatCurrency, setFiatCurrency] = useState('USD');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [paymentResult, setPaymentResult] = useState<GenerateLinkResponse | null>(null);
  const [currencySearchTerm, setCurrencySearchTerm] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [minimumAmount, setMinimumAmount] = useState(0.5);
  const [minimumAmountLoading, setMinimumAmountLoading] = useState(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);

  const [selectedProviders, setSelectedProviders] = useState<Set<string>>(
    () => new Set(FIAT_PROVIDER_OPTIONS.map((p) => p.id))
  );

  const [invoiceNumber, setInvoiceNumber] = useState(generateInvoiceNumber);
  const [issueDate, setIssueDate] = useState(() => formatISODate(new Date()));
  const [dueDate, setDueDate] = useState(() => addDaysISO(new Date(), 14));
  const [yourCompany, setYourCompany] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(() => [
    { id: newLineId(), name: '', qty: 1, price: 0 },
  ]);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [yourAddress, setYourAddress] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [taxRatePercent, setTaxRatePercent] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');

  const userRole = getCurrentUserRole();
  const currentUserId = getCurrentUserId();
  const username = localStorage.getItem('username') || '';
  const avatarLetter = (username.trim().charAt(0) || 'L').toUpperCase();

  const filteredCurrencies = useMemo(
    () =>
      currencySearchTerm.trim() === ''
        ? FIAT_CURRENCIES
        : FIAT_CURRENCIES.filter((c) => c.toLowerCase().includes(currencySearchTerm.toLowerCase())),
    [currencySearchTerm]
  );

  const { subtotal, taxTotal, invoiceTotal } = useMemo(() => {
    const sub = lineItems.reduce((acc, row) => acc + round2(Math.max(0, row.qty) * Math.max(0, row.price)), 0);
    const rate = parseFloat(taxRatePercent);
    const taxPct = Number.isFinite(rate) && rate > 0 ? rate : 0;
    const tax = round2(sub * (taxPct / 100));
    return { subtotal: round2(sub), taxTotal: tax, invoiceTotal: round2(sub + tax) };
  }, [lineItems, taxRatePercent]);

  useEffect(() => {
    const loadUsers = async () => {
      if (userRole !== 'Admin' && userRole !== 'SuperAgent') return;
      setUsersLoading(true);
      setUsersError(null);
      try {
        const allUsers = await dashboardService.getUsers();
        if (userRole === 'Admin') {
          setUsers(allUsers.filter((u) => u.role !== 'Admin'));
        } else {
          const currentUser = allUsers.find((u) => u.id.toString() === currentUserId);
          const agentUsers = allUsers.filter((u) => u.role === 'Agent');
          let filtered: User[];
          if (currentUser) {
            filtered = [currentUser, ...agentUsers];
          } else {
            const superAgentUser: User = {
              id: parseInt(currentUserId || '0', 10),
              name: username || 'SuperAgent User',
              email: '',
              phone: '',
              usdtWallet: '',
              role: 'SuperAgent',
            };
            filtered = [superAgentUser, ...agentUsers];
          }
          setUsers(filtered);
          if (currentUserId) setUserId(currentUserId);
          setUsersLoading(false);
          return;
        }
      } catch (e) {
        console.error(e);
        setUsersError('Failed to load users');
      } finally {
        setUsersLoading(false);
      }
    };

    if (userRole === 'Agent' && currentUserId) {
      setUserId(currentUserId);
    }
    loadUsers();
  }, [userRole, currentUserId, username]);

  useEffect(() => {
    setCurrencySearchTerm(fiatCurrency);
  }, [fiatCurrency]);

  useEffect(() => {
    const run = async () => {
      setMinimumAmountLoading(true);
      try {
        const min = await exchangeRateService.getMinimumAmount(fiatCurrency);
        setMinimumAmount(min);
      } catch {
        setMinimumAmount(0.5);
      } finally {
        setMinimumAmountLoading(false);
      }
    };
    if (fiatCurrency) run();
  }, [fiatCurrency]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setShowCurrencyDropdown(false);
      }
    };
    if (showCurrencyDropdown) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showCurrencyDropdown]);

  const handleCurrencySelect = async (currency: string) => {
    setFiatCurrency(currency);
    setCurrencySearchTerm(currency);
    setShowCurrencyDropdown(false);
    try {
      const min = await exchangeRateService.getMinimumAmount(currency);
      setMinimumAmount(min);
    } catch {
      setMinimumAmount(0.5);
    }
  };

  const toggleProvider = (id: string) => {
    setSelectedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setFormError(null);
  };

  const selectAllProviders = () => {
    setSelectedProviders(new Set(FIAT_PROVIDER_OPTIONS.map((p) => p.id)));
  };

  const clearProviders = () => {
    setSelectedProviders(new Set());
  };

  const updateLine = useCallback((id: string, patch: Partial<InvoiceLineItem>) => {
    setLineItems((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setFormError(null);
  }, []);

  const addLine = () => {
    setLineItems((rows) => [...rows, { id: newLineId(), name: '', qty: 1, price: 0 }]);
  };

  const removeLine = (id: string) => {
    setLineItems((rows) => {
      const next = rows.filter((r) => r.id !== id);
      return next.length ? next : [{ id: newLineId(), name: '', qty: 1, price: 0 }];
    });
    setFormError(null);
  };

  const activeLines = lineItems.filter((r) => r.name.trim() !== '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    if (selectedProviders.size === 0) {
      setFormError('Select at least one fiat provider.');
      setSubmitting(false);
      return;
    }

    if (!invoiceNumber.trim()) {
      setFormError('Enter an invoice number.');
      setSubmitting(false);
      return;
    }

    if (activeLines.length === 0) {
      setFormError('Add at least one invoice line with an item name.');
      setSubmitting(false);
      return;
    }

    const invalid = activeLines.some((r) => r.qty <= 0 || r.price < 0);
    if (invalid) {
      setFormError('Each line needs a quantity of at least 1 and a non-negative price.');
      setSubmitting(false);
      return;
    }

    if (invoiceTotal < minimumAmount) {
      const minFormatted = exchangeRateService.formatMinimum(fiatCurrency, minimumAmount);
      setFormError(`Invoice total must be at least ${minFormatted} for provider availability.`);
      setSubmitting(false);
      return;
    }

    if (!userId) {
      setFormError('Please select a user');
      setSubmitting(false);
      return;
    }

    try {
      const requestData: GenerateLinkRequest = {
        userId: parseInt(userId, 10),
        fiatCurrency,
        amount: invoiceTotal,
      };
      const result = await paymentLinkService.generatePaymentLinks(requestData);
      const filtered = filterLinksByProviders(result.paymentLinks, selectedProviders);
      if (filtered.length === 0) {
        setFormError(
          'No payment links matched your provider selection. Try selecting more providers or use the full recommended mix.'
        );
        setSubmitting(false);
        return;
      }
      const firstLink = filtered[0];
      const sessionId = firstLink ? extractPaySessionId(firstLink) : null;

      if (sessionId) {
        navigate(checkoutPathForSession(sessionId));
        const checkoutUrl = checkoutUrlForSession(sessionId);
        navigator.clipboard?.writeText(checkoutUrl)?.catch(() => {});
        return;
      }

      setPaymentResult({ ...result, paymentLinks: filtered });
      setShowResult(true);
    } catch (err) {
      console.error(err);
      setFormError(extractApiErrorMessage(err, 'Failed to issue invoice'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const inputClass =
    'w-full rounded-lg border border-[#d1d9e0] bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25';
  const labelClass = 'mb-1.5 block text-xs font-semibold text-slate-700';
  const sectionShell = 'rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm';

  const canSubmit = userId && invoiceTotal >= minimumAmount && activeLines.length > 0 && !minimumAmountLoading;

  return (
    <div className="homeBg pb-28 pt-6">
      <div className="mx-auto w-full max-w-7xl rounded-2xl bg-white p-5 px-4 blurLayerInteractiveInteractive">
        <nav className="mb-4 text-xs text-slate-500">
          <Link to="/dashboard" className="text-slate-500 hover:text-[#2297D1]">
            Payments
          </Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-slate-700">New Invoice</span>
        </nav>

        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex w-full items-start gap-3 border-b border-slate-200 pb-4">
          
            <div className="min-w-0 flex-1">
              <h1 className="mb-0 text-2xl font-bold tracking-tight text-slate-900 lg:text-[48px]">Issue Invoice</h1>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex-1 overflow-y-auto">
            {!showResult ? (
              <form id="issue-invoice-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="rounded-xl border border-[#e2e8f0] bg-[#f1f5f9] px-4 py-4">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Invoice composer
                  </p>
                  <p className="mb-2 text-sm font-semibold text-slate-800">
                    Build a simple invoice and turn it into a shareable checkout total.
                  </p>
                  <p className="mb-0 text-sm leading-relaxed text-slate-600">
                    Line items set the amount we send to providers. Recipient and provider mix work the same way as a
                    standard payment link.
                  </p>
                </div>

                {(usersError || formError) && (
                  <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {usersError || formError}
                  </div>
                )}

                <section className={sectionShell}>
                  <h2 className="mb-1 text-base font-semibold text-slate-900">Basics</h2>
                  <p className="mb-4 text-sm text-slate-600">
                    Currency applies to every amount on this invoice. The total is derived from your line items (and optional
                    tax).
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Fiat Currency</label>
                      <div ref={currencyDropdownRef} className="relative">
                        <input
                          type="text"
                          className={inputClass}
                          value={currencySearchTerm}
                          onChange={(e) => {
                            setCurrencySearchTerm(e.target.value);
                            setShowCurrencyDropdown(true);
                          }}
                          onFocus={() => {
                            setShowCurrencyDropdown(true);
                            setCurrencySearchTerm('');
                          }}
                          placeholder="Search currency..."
                          autoComplete="off"
                        />
                        {showCurrencyDropdown && (
                          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-lg border border-[#e2e8f0] bg-white py-1 shadow-lg">
                            {filteredCurrencies.length > 0 ? (
                              filteredCurrencies.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                                  onClick={() => handleCurrencySelect(c)}
                                >
                                  {c}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm italic text-slate-500">No currencies match</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Invoice Total</label>
                      <input
                        type="text"
                        readOnly
                        className={cn(inputClass, 'cursor-not-allowed bg-slate-50 text-slate-700')}
                        value={
                          minimumAmountLoading
                            ? 'Calculating…'
                            : `${invoiceTotal.toFixed(2)} ${fiatCurrency} — Calculated from invoice items`
                        }
                      />
                      <p className="mt-1.5 text-xs text-slate-500">
                        Total must be at least {exchangeRateService.formatMinimum(fiatCurrency, minimumAmount)} for provider
                        availability
                        {minimumAmountLoading ? ' (loading minimum…)' : ''}.
                      </p>
                    </div>
                  </div>
                </section>

                <section className={sectionShell}>
                  <h2 className="mb-1 text-base font-semibold text-slate-900">Recipient</h2>
                  <p className="mb-4 text-sm text-slate-600">
                    Choose which account should own this payment page and appear in reporting.
                  </p>
                  {userRole !== 'Agent' ? (
                    <>
                      <label className={labelClass}>Select User</label>
                      {usersLoading ? (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Spinner animation="border" size="sm" />
                          Loading users...
                        </div>
                      ) : (
                        <select
                          className={inputClass}
                          value={userId}
                          onChange={(e) => {
                            setUserId(e.target.value);
                            setFormError(null);
                          }}
                        >
                          <option value="">Select a User</option>
                          {users.map((u, index) => (
                            <option key={u.id} value={u.id}>
                              {userRole === 'SuperAgent' && index === 0 ? '\u{1F464} ' : ''}
                              {u.name} ({u.role})
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  ) : (
                    <>
                      <label className={labelClass}>Account</label>
                      <input
                        className={cn(inputClass, 'bg-slate-50')}
                        readOnly
                        value={username ? `${username} (Agent)` : 'Your account'}
                      />
                    </>
                  )}
                </section>

                <section className={sectionShell}>
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="mb-1 text-base font-semibold text-slate-900">Fiat Providers</h2>
                      <p className="text-sm text-slate-600">
                        Choose which fiat providers should appear on the checkout page.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllProviders}
                        className="rounded-lg border border-[#d1d9e0] bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={clearProviders}
                        className="rounded-lg border border-[#d1d9e0] bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {FIAT_PROVIDER_OPTIONS.map((p) => (
                      <label
                        key={p.id}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5"
                      >
                        <input
                          type="checkbox"
                          checked={selectedProviders.has(p.id)}
                          onChange={() => toggleProvider(p.id)}
                          className="h-4 w-4 rounded border-slate-300 text-[#2297D1] focus:ring-[#2297D1]"
                        />
                        <span className="text-sm font-medium text-slate-800">{p.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-3 mb-0 text-xs text-slate-500">
                    Provider availability is recalculated automatically as the invoice total and currency change.
                  </p>
                </section>

                <section className={sectionShell}>
                  <h2 className="mb-1 text-base font-semibold text-slate-900">Invoice details</h2>
                  <p className="mb-4 text-sm text-slate-600">Reference metadata and line items for this request.</p>

                  <div className="mb-4 grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className={labelClass}>Invoice Number</label>
                      <input
                        className={inputClass}
                        value={invoiceNumber}
                        onChange={(e) => {
                          setInvoiceNumber(e.target.value);
                          setFormError(null);
                        }}
                        placeholder="INV-20260328-615"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Issue Date</label>
                      <input
                        type="date"
                        className={inputClass}
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Due Date</label>
                      <input
                        type="date"
                        className={inputClass}
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-6 grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>Your Company Name</label>
                      <input
                        className={inputClass}
                        value={yourCompany}
                        onChange={(e) => setYourCompany(e.target.value)}
                        placeholder="Your business"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Client Company Name</label>
                      <input
                        className={inputClass}
                        value={clientCompany}
                        onChange={(e) => setClientCompany(e.target.value)}
                        placeholder="Client business"
                      />
                    </div>
                  </div>

                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="mb-0 text-sm font-semibold text-slate-900">Line items</h3>
                    <button
                      type="button"
                      onClick={addLine}
                      className="rounded-lg border border-[#d1d9e0] bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Add Item
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-[#e2e8f0]">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead className="border-b border-[#e2e8f0] bg-[#f8fafc] text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Item Name</th>
                          <th className="w-24 px-3 py-2">Qty</th>
                          <th className="w-28 px-3 py-2">Price</th>
                          <th className="w-28 px-3 py-2">Amount</th>
                          <th className="w-12 px-2 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((row) => {
                          const amt = round2(Math.max(0, row.qty) * Math.max(0, row.price));
                          return (
                            <tr key={row.id} className="border-b border-[#f1f5f9] last:border-b-0">
                              <td className="px-3 py-2 align-middle">
                                <input
                                  className={inputClass}
                                  value={row.name}
                                  onChange={(e) => updateLine(row.id, { name: e.target.value })}
                                  placeholder="Description"
                                />
                              </td>
                              <td className="px-3 py-2 align-middle">
                                <input
                                  type="number"
                                  min={1}
                                  step={1}
                                  className={inputClass}
                                  value={row.qty || ''}
                                  onChange={(e) =>
                                    updateLine(row.id, { qty: Math.max(0, parseInt(e.target.value, 10) || 0) })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2 align-middle">
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className={inputClass}
                                  value={row.price || ''}
                                  onChange={(e) =>
                                    updateLine(row.id, { price: Math.max(0, parseFloat(e.target.value) || 0) })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2 align-middle">
                                <span className="text-slate-700">{amt.toFixed(2)}</span>
                              </td>
                              <td className="px-2 py-2 align-middle text-center">
                                <button
                                  type="button"
                                  onClick={() => removeLine(row.id)}
                                  className="rounded p-1 text-red-600 hover:bg-red-50"
                                  aria-label="Remove line"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <button
                    type="button"
                    onClick={() => setOptionalOpen((o) => !o)}
                    className="mt-4 flex w-full items-center justify-between rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 text-left text-sm font-semibold text-slate-800"
                  >
                    <span>Optional invoice details</span>
                    {optionalOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {optionalOpen && (
                    <div className="mt-3 space-y-4 rounded-lg border border-dashed border-[#cbd5e1] bg-white p-4">
                      <p className="text-xs text-slate-500">
                        Add addresses, a tax rate, or internal notes. Notes are not sent to the payment API today.
                      </p>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className={labelClass}>Your address</label>
                          <textarea
                            className={cn(inputClass, 'min-h-[80px] resize-y')}
                            value={yourAddress}
                            onChange={(e) => setYourAddress(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Client address</label>
                          <textarea
                            className={cn(inputClass, 'min-h-[80px] resize-y')}
                            value={clientAddress}
                            onChange={(e) => setClientAddress(e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Tax / VAT rate (%)</label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          className={inputClass}
                          value={taxRatePercent}
                          onChange={(e) => setTaxRatePercent(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Notes</label>
                        <textarea
                          className={cn(inputClass, 'min-h-[72px] resize-y')}
                          value={invoiceNotes}
                          onChange={(e) => setInvoiceNotes(e.target.value)}
                          placeholder="Terms, PO number, etc."
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-6 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4">
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Subtotal</span>
                      <span>
                        {subtotal.toFixed(2)} {fiatCurrency}
                      </span>
                    </div>
                    <div className="mt-2 flex justify-between text-sm text-slate-600">
                      <span>Tax / VAT total</span>
                      <span>
                        {taxTotal.toFixed(2)} {fiatCurrency}
                      </span>
                    </div>
                    <div
                      className="mt-3 flex justify-between border-t border-[#e2e8f0] pt-3 text-base font-semibold"
                      style={{ color: BRAND }}
                    >
                      <span>Total</span>
                      <span>
                        {invoiceTotal.toFixed(2)} {fiatCurrency}
                      </span>
                    </div>
                  </div>
                </section>
              </form>
            ) : (
              paymentResult && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                    Checkout links for invoice <strong>{invoiceNumber}</strong> are ready. The first link was copied to your
                    clipboard.
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">Your links</h2>
                  <ul className="space-y-3">
                    {paymentResult.paymentLinks.map((link, index) => {
                      const url = extractUrl(link);
                      return (
                        <li
                          key={index}
                          className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4 text-sm text-slate-800"
                        >
                          <div className="mb-2 break-all font-mono text-xs text-slate-700">{url}</div>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(url)}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#2297D1] hover:bg-white"
                          >
                            Copy
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResult(false);
                        setPaymentResult(null);
                        setFormError(null);
                        setInvoiceNumber(generateInvoiceNumber());
                      }}
                      className="rounded-lg border border-[#d1d9e0] bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                    >
                      Issue another
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/transactions')}
                      className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95"
                      style={{ backgroundColor: BRAND }}
                    >
                      View transactions
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {!showResult && (
            <div className="flex shrink-0 justify-end gap-3 border-t border-[#e2e8f0] bg-white px-0 py-4 sm:px-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="rounded-lg border border-[#d1d9e0] bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="issue-invoice-form"
                disabled={submitting || !canSubmit}
                className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-50"
                style={{ backgroundColor: BRAND }}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner animation="border" size="sm" className="!h-4 !w-4 border-white" />
                    Issuing…
                  </span>
                ) : (
                  'Issue Invoice'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssueInvoicePage;
